import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../firebase';
import { loadStripe } from '@stripe/stripe-js';
import { ThemeProvider } from '../../contexts/ThemeContext';
import { buildSubscriptionUrl } from '../../config/env';
import { useSubscriptionManager } from '../../hooks/chat/useSubscriptionManager';
import { SUBSCRIPTION_TIERS, formatPrice, annualSavings } from './subscriptionPlans';
import './SubscriptionScreen.css';

const stripePromise = loadStripe('pk_live_51Q00slApEIymxqdxJNihHzZW4I4gW5AFyn8KBg1GmF8zD5wNMBglrb0yenVIULN2hUT1FlemPhUCpBvT7DUrtfh000C6iBwaSW');
const STRIPE_MANAGEMENT_URL = 'https://billing.stripe.com/p/login/eVa8xn3QngOZdEYcMM';
const APP_STORE_MANAGEMENT_URL = 'https://apps.apple.com/account/subscriptions';
const CHECKOUT_ERRORS = {
  invalid_plan: 'This plan is currently unavailable. Please contact billing support.',
  unauthenticated: 'Your sign-in expired. Sign in again to subscribe.',
  account_mismatch: 'Your account changed. Please try again.',
  entitlement_unavailable: 'Couldn’t verify your current plan. Wait a moment, then retry.',
  billing_sync_pending: 'Your billing status is updating. Wait a moment, then retry.',
  checkout_in_progress: 'Another checkout is still opening. Wait a moment, then retry.',
};

const SubscriptionScreen = () => {
  const navigate = useNavigate();
  const checkoutAttempt = useRef(null);
  const checkoutInFlight = useRef(false);
  const checkoutPanel = useRef(null);
  const [checkoutHeight, setCheckoutHeight] = useState(180);
  const [selectedPriceId, setSelectedPriceId] = useState(SUBSCRIPTION_TIERS[0].prices[0].priceId);
  const [processingPriceId, setProcessingPriceId] = useState(null);
  const [error, setError] = useState(null);
  const {
    hasSubscription, hasMaxSubscription, subscriptionSources,
    isCheckingSubscription, subscriptionStatus, checkSubscription,
  } = useSubscriptionManager();
  const currentTier = hasMaxSubscription ? 'Max' : 'Pro';
  const verificationUnavailable = subscriptionStatus === 'unknown' && !isCheckingSubscription;
  const cannotPurchase = isCheckingSubscription || verificationUnavailable || hasSubscription || processingPriceId !== null;
  const selectedTier = SUBSCRIPTION_TIERS.find(tier => tier.prices.some(price => price.priceId === selectedPriceId));
  const selectedPrice = selectedTier.prices.find(price => price.priceId === selectedPriceId);

  useEffect(() => {
    const panel = checkoutPanel.current;
    if (!panel || typeof ResizeObserver === 'undefined') return;
    // Reserve enough scroll space even when an error or larger text
    // makes the fixed checkout panel taller.
    const updateHeight = () => setCheckoutHeight(Math.ceil(panel.getBoundingClientRect().height));
    const observer = new ResizeObserver(updateHeight);
    observer.observe(panel);
    updateHeight();
    return () => observer.disconnect();
  }, []);

  const handleSubscription = async priceId => {
    if (checkoutInFlight.current || cannotPurchase) return;
    checkoutInFlight.current = true;
    setProcessingPriceId(priceId);
    setError(null);
    const currentUser = auth.currentUser;
    try {
      if (!currentUser) throw new Error('Sign in to subscribe.');
      const token = await currentUser.getIdToken();
      const checkAccount = () => {
        if (auth.currentUser?.uid !== currentUser.uid) throw new Error('Your account changed. Please try again.');
      };
      checkAccount();
      if (!checkoutAttempt.current || checkoutAttempt.current.priceId !== priceId || checkoutAttempt.current.uid !== currentUser.uid) {
        checkoutAttempt.current = { uid: currentUser.uid, priceId, key: crypto.randomUUID() };
      }
      const headers = {
        'Content-Type': 'application/json', Authorization: 'Bearer ' + token,
        'Idempotency-Key': checkoutAttempt.current.key,
      };
      const customerResponse = await fetch(buildSubscriptionUrl('/api/create-stripe-customer'), {
        method: 'POST', headers, body: JSON.stringify({ uid: currentUser.uid, email: currentUser.email }),
      });
      const customerData = await customerResponse.json();
      checkAccount();
      if (!customerResponse.ok || !customerData.success) {
        throw new Error(CHECKOUT_ERRORS[customerData.error?.code] || 'Couldn’t prepare checkout. Please retry.');
      }

      const sessionResponse = await fetch(buildSubscriptionUrl('/api/create-checkout-session'), {
        method: 'POST', headers, body: JSON.stringify({ uid: currentUser.uid, priceId }),
      });
      const sessionData = await sessionResponse.json();
      checkAccount();
      if (!sessionResponse.ok || !sessionData.sessionId) {
        if (sessionData.error?.code === 'already_subscribed') {
          await checkSubscription();
          throw new Error('Your subscription is already active. Manage it where you subscribed to change plans.');
        }
        throw new Error(CHECKOUT_ERRORS[sessionData.error?.code] || 'Checkout could not be started. Please retry.');
      }
      const stripe = await stripePromise;
      checkAccount();
      const result = await stripe.redirectToCheckout({ sessionId: sessionData.sessionId });
      if (result.error) throw new Error(result.error.message);
    } catch (err) {
      setError(err.message);
    } finally {
      checkoutInFlight.current = false;
      setProcessingPriceId(null);
    }
  };

  return (
    <ThemeProvider>
      <main className="subscription-page theme-app-bg" style={{ '--subscription-checkout-height': checkoutHeight + 'px' }}>
        <div className="subscription-container">
          <button type="button" className="subscription-back" onClick={() => navigate('/chat')}>
            <span aria-hidden="true">←</span> Back to Chat
          </button>
          <header className="subscription-heading">
            <h1>Choose your Arrow.AI plan</h1>
            <p>Pro for Chat. Max adds Work.</p>
            <nav className="subscription-mobile-plans" aria-label="View a plan">
              <a href="#plan-pro">View Pro</a>
              <a href="#plan-max">View Max</a>
            </nav>
          </header>

          {hasSubscription && (
            <div className="subscription-status" role="status">
              <div>
                <strong>Your {currentTier} plan is active.</strong>
                <p>Change plans or manage billing where you subscribed.</p>
              </div>
              <div className="subscription-management">
                {subscriptionSources.includes('stripe') && <a href={STRIPE_MANAGEMENT_URL}>Manage Stripe Subscription</a>}
                {subscriptionSources.includes('appStore') && <a href={APP_STORE_MANAGEMENT_URL}>Manage in App Store</a>}
                {subscriptionSources.includes('specialAccess') && !subscriptionSources.some(source => ['stripe', 'appStore'].includes(source)) &&
                  <span>Your access is complimentary.</span>}
              </div>
            </div>
          )}
          {verificationUnavailable && (
            <div className="subscription-status" role="alert">
              <p>Couldn’t verify your current plan. Retry before starting another purchase.</p>
              <button type="button" onClick={checkSubscription}>Retry Verification</button>
            </div>
          )}
          <fieldset className="subscription-options">
            <legend>Choose one plan and billing option</legend>
            <div className="subscription-tier-grid">
              {SUBSCRIPTION_TIERS.map(tier => (
                <section key={tier.id} id={'plan-' + tier.id} className={'subscription-tier subscription-tier-' + tier.id} aria-labelledby={'tier-' + tier.id}>
                  <div className="subscription-tier-heading">
                    <div>
                      <h2 id={'tier-' + tier.id}>
                        <span className={tier.id === 'max' ? 'subscription-max-title' : undefined}>{tier.name}</span>
                      </h2>
                      <p>{tier.description}</p>
                    </div>
                    <span className={'subscription-tier-badge' + (tier.id === 'max' ? ' subscription-max-badge' : '')}>
                      {tier.id === 'max' ? 'Chat + Work' : 'Chat'}
                    </span>
                  </div>
                  <ul className="subscription-benefits">
                    {tier.benefits.map(benefit => (
                      <li key={benefit}><span aria-hidden="true">✓</span>{benefit}</li>
                    ))}
                  </ul>
                  <p className="subscription-tier-note">{tier.note}</p>

                  <div className="subscription-billing">
                    <h3>{tier.name} billing</h3>
                    {tier.prices.map(price => (
                      <label key={price.priceId} className={'subscription-price-option' + (price.priceId === selectedPriceId ? ' is-selected' : '')}>
                        <input
                          type="radio" name="subscription-plan" value={price.priceId}
                          aria-labelledby={'tier-' + tier.id + ' option-' + price.priceId + ' amount-' + price.priceId}
                          checked={price.priceId === selectedPriceId}
                          disabled={processingPriceId !== null}
                          onChange={() => {
                            setSelectedPriceId(price.priceId);
                            setError(null);
                          }}
                        />
                        <span className="subscription-price-copy" id={'option-' + price.priceId}>
                          <span className="subscription-price-title">
                            <span>{price.label}</span>
                            {price.period === 'year' && annualSavings(tier) > 0 &&
                              <span className="subscription-savings">Save {annualSavings(tier)}%</span>}
                          </span>
                          <span className="subscription-price-detail">
                            {price.period === 'year' ? formatPrice(price.amount / 12) + '/month, billed yearly' : 'Billed ' + (price.period === 'month' ? 'monthly' : 'weekly')}
                          </span>
                        </span>
                        <span className="subscription-price-amount" id={'amount-' + price.priceId}>
                          <strong>{formatPrice(price.amount)}</strong>
                          <span>/{price.period}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </fieldset>

          <div className="subscription-checkout" ref={checkoutPanel}>
            {error && <p className="subscription-error" role="alert">{error}</p>}
            <div className="subscription-selection-summary" aria-live="polite" aria-atomic="true">
              <strong>{selectedTier.name} · {selectedPrice.label}</strong>
              <p>{formatPrice(selectedPrice.amount)}/{selectedPrice.period}. Auto-renews; cancel anytime.</p>
            </div>
            <button
              type="button" className="subscription-purchase"
              disabled={cannotPurchase}
              onClick={() => handleSubscription(selectedPriceId)}
            >
              {processingPriceId !== null ? 'Opening Checkout…' :
                isCheckingSubscription ? 'Checking Your Plan…' :
                hasSubscription ? 'Manage Your Current Plan Above' :
                'Continue with ' + selectedTier.name + ' · ' + selectedPrice.label}
            </button>
          </div>

          <footer className="subscription-footer">
            <p>Prices in USD. Confirm the final amount and renewal terms at checkout.</p>
            <div>
              <a href="https://tryarrow.ai/terms">Terms</a>
              <a href="https://tryarrow.ai/privacy">Privacy</a>
              <a href="mailto:anirban@arrow-ai.com?subject=Billing%20Support%20Request">Billing help</a>
            </div>
          </footer>
        </div>
      </main>
    </ThemeProvider>
  );
};

export default SubscriptionScreen;
